import React, { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import clsx from 'clsx';
import _get from 'lodash/get';

// bonnet
import { useDevice } from '@bonnet/next/device';
import { ErrorBoundary } from '@bonnet/next/error-boundary';

import { eventBus } from '@atc/cai-event-bus';
import { useFeatures } from '@atc/features';

import {
    Button,
    Glyphicon,
    Heading,
    OverlayTrigger,
    Popover,
    Text,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { psxVDPPopoverCtaClick } from 'reaxl-analytics-handlers';
import { EmailCTA } from 'reaxl-email';
import {
    HotCar,
    NewlyListed,
    ReducedPrice,
} from 'reaxl-molecules';

// fetchers
import {
    vdpResultsDuck,
} from '@/ducks/vdp';

import { viewCountFetcher } from '@/fetchers';

import KBBConsumerRatings from './RatingsAndReviews/KBBConsumerRatings';
import SaveAndShareContainer from './SaveAndShareContainer';

export function VehicleDetailsTitleContainer({
    className,
    showRatingsAndReviews,
    onEmailCTAClick,
}) {
    const device = useDevice();
    const isGreaterThanSm = _get(device, 'greaterThan.sm', false);

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const {
        enable_view_count: [isViewCountEnabled, {
            min_days: minDays,
            min_views: minViews,
            date_range: dateRange,
        }],
        vdp_mobile_lead_cta_relocation: [isLeadRelocationEnabled],
    } = useFeatures([
        'enable_view_count',
        'vdp_mobile_lead_cta_relocation',
    ]);

    const [count, setCount] = useState(null);

    const {
        daysOnSite,
        isHot,
        isNewlyListed,
        isReducedPrice,
        id: listingId,
        kbbConsumerRatings,
        kbbConsumerReviewCount,
        listingTitlePSX,
        owner: { privateSeller, hideEmailForm } = {},
        pricingDetail,
        title,
        vin,
    } = listing;

    const listingTitle = (privateSeller && listingTitlePSX) ? listingTitlePSX : title;

    const showVehicleBadges = !isLeadRelocationEnabled && (isHot || isNewlyListed || isReducedPrice || count);

    const enableSaveAndShare = isGreaterThanSm && !isLeadRelocationEnabled;

    const hasDealIndicator = pricingDetail?.dealIndicator ?? false;

    const getViewCount = async () => {
        const theCount = await viewCountFetcher({ vin, dateRange });
        const { KBB = 0, ATC = 0 } = theCount;
        const totalCount = KBB + ATC;
        if (daysOnSite > minDays && totalCount > minViews) setCount(totalCount);
    };

    useEffect(() => {
        if (isViewCountEnabled && !isHot) {
            getViewCount();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vin]);

    const viewCountClassNames = clsx({
        'margin-left-4': (isNewlyListed || isReducedPrice),
        'margin-left-auto margin-left-sm-0': !(isNewlyListed || isReducedPrice),
    });

    const isWebComponentReady = useSelector(vdpResultsDuck.selectors.getIsWebComponentReady);

    const handleMakeOffer = (event) => {
        eventBus.emit('psx.listingActions.openMakeOffer', {});
        sendClick(psxVDPPopoverCtaClick, event, { listingId });
    };

    const handleBadgeClick = (event, labelText) => {
        sendClick('inventoryBadgeClick', event, { labelText });
    };

    const newlyListedPopover = (
        <Popover
            id="contextual-help-newly-listed"
        >
            This newly listed vehicle has been on the market for less than a month.
            {' '}
            {privateSeller && isWebComponentReady && (
                <Button
                    bsStyle="text-decorated"
                    onClick={handleMakeOffer}
                >
                    Submit an offer
                </Button>
            )}
            {!privateSeller && !hideEmailForm && (
                <EmailCTA
                    className="display-inline-block"
                    labelKey="newlyListedPopover"
                    onClick={onEmailCTAClick}
                    type="link-decorated"
                    listingId={listingId}
                    ownerId={listing?.owner?.id}
                    allowPropagation
                />
            )}
            { ((privateSeller && isWebComponentReady) || !hideEmailForm)
                && (
                    <>
                        {' '}
                        before other interested buyers.
                    </>
                )}
        </Popover>
    );

    const reducedPricePopover = (
        <Popover
            id="contextual-help-reduced-price"
        >
            The seller dropped the price on this car!
            {' '}
            {privateSeller && isWebComponentReady && (
                <Button
                    bsStyle="text-decorated"
                    className="display-inline-block"
                    onClick={handleMakeOffer}
                >
                    Submit an Offer
                </Button>
            )}
            {!privateSeller && !hideEmailForm && (
                <EmailCTA
                    className="display-inline-block"
                    labelKey="submitAnOffer"
                    onClick={onEmailCTAClick}
                    type="link-decorated"
                    listingId={listingId}
                    ownerId={listing?.owner?.id}
                    allowPropagation
                />
            )}
        </Popover>
    );

    const renderUrgencyDriver = (() => {
        if (isHot) {
            return <HotCar description="Highly likely to sell within a week" />;
        }

        if (isNewlyListed) {
            if (daysOnSite > 0 && daysOnSite < 30) {
                return (
                    <OverlayTrigger
                        trigger="click"
                        placement="top"
                        rootClose
                        overlay={newlyListedPopover}
                        onClick={(event) => handleBadgeClick(event, `Listed ${daysOnSite} Day${daysOnSite > 1 ? 's' : ''} Ago`)}
                        className="cursor-pointer"
                    >
                        <NewlyListed />
                    </OverlayTrigger>
                );
            }
            return <NewlyListed />;
        }

        if (isReducedPrice) {
            const labelText = 'Recent Price Drop';
            return (
                <OverlayTrigger
                    trigger="click"
                    placement="top"
                    rootClose
                    overlay={reducedPricePopover}
                    onClick={(event) => handleBadgeClick(event, labelText)}
                    className="cursor-pointer"
                >
                    <ReducedPrice label={labelText} />
                </OverlayTrigger>
            );
        }

        return null;
    });

    return (
        <div
            data-cmp="listingTitleContainer"
            className={clsx('row padding-horizontal-2', className)}
        >
            <div>
                <ErrorBoundary>
                    {showVehicleBadges ? (
                        <div
                            className="display-flex align-items-center justify-content-between"
                        >
                            <div>
                                {renderUrgencyDriver()}
                                {!!count && (
                                    <Text
                                        className={viewCountClassNames}
                                        data-cmp="vdp-view-count"
                                        data-testid="vdp-view-count"
                                        color="subdued-lightest"
                                        size={200}
                                        weight="bold"
                                    >
                                        <Glyphicon glyph="eye-outline" />
                                        {` ${count} view${count === 1 ? '' : 's'}`}
                                    </Text>
                                )}
                            </div>
                            {enableSaveAndShare && <SaveAndShareContainer />}
                        </div>
                    ) : enableSaveAndShare && <SaveAndShareContainer />}
                </ErrorBoundary>
                <div className={clsx({
                    'margin-bottom-4': hasDealIndicator && !showRatingsAndReviews && !isLeadRelocationEnabled,
                    'margin-top-4 margin-bottom-3': isLeadRelocationEnabled,
                }, 'display-flex align-items-center margin-bottom-sm-0 margin-bottom-1')}
                >
                    <Heading
                        level={3}
                        className={clsx(
                            {
                                'margin-bottom-sm-4': !showRatingsAndReviews && !isLeadRelocationEnabled,
                                'margin-bottom-sm-5': !showRatingsAndReviews && isLeadRelocationEnabled,
                            },
                        )}
                        componentClass="h1"
                        id="vehicle-details-heading"
                    >
                        {listingTitle}
                    </Heading>
                </div>
                {showRatingsAndReviews
                    && (
                        <KBBConsumerRatings
                            className="margin-bottom-2 margin-bottom-sm-4"
                            kbbConsumerRatings={kbbConsumerRatings}
                            kbbConsumerReviewCount={kbbConsumerReviewCount}
                        />
                    )}
            </div>
        </div>
    );
}

export default VehicleDetailsTitleContainer;
